<template>
  <v-container>
    <v-form ref="form" :lazy-validation="true">
      <validation-observer ref="validationObserver" v-slot="{ invalid }">
        <v-row class="mb-2">
          <template v-if="contactType === 'mailingList'">
            <v-col cols="6">
              <validation-provider
                :name="$t('form.fields.firstName')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="firstName"
                  :label="$t('form.fields.firstName') + ' *'"
                  required
                  outlined
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="6">
              <validation-provider
                :name="$t('form.fields.lastName')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="lastName"
                  :label="$t('form.fields.lastName') + ' *'"
                  required
                  outlined
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col class="mt-n8">
              <v-text-field
                v-model="organization"
                :label="$t('form.fields.organization')"
                outlined
              ></v-text-field>
            </v-col>
          </template>
          <v-col v-else>
            <validation-provider
              :name="$t('form.fields.name')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                v-model="name"
                :label="$t('form.fields.name') + ' *'"
                required
                outlined
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-2">
          <v-col cols="6">
            <validation-provider
              :name="$t('form.fields.email')"
              v-slot="{ errors }"
              vid="originalEmail"
              rules="required|email|confirmed:@confirmationEmail"
            >
              <v-text-field
                ref="originalMail"
                v-model="email"
                :label="$t('form.fields.email') + ' *'"
                required
                outlined
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6">
            <validation-provider
              :name="$t('form.fields.confirmationEmail')"
              v-slot="{ errors }"
              vid="confirmationEmail"
              rules="required|email|confirmed:@originalEmail"
            >
              <v-text-field
                v-model="confirmationEmail"
                :label="$t('form.fields.confirmationEmail') + ' *'"
                required
                outlined
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-2" v-if="contactType !== 'mailingList'">
          <v-col>
            <validation-provider
              :name="$t('form.fields.subject')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                outlined
                v-model="subject"
                required
                :label="$t('form.fields.subject') + ' *'"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
        </v-row>

        <v-row class="mt-n8" v-if="contactType !== 'mailingList'">
          <v-col>
            <validation-provider
              :name="$t('form.fields.note')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-textarea
                outlined
                v-model="note"
                required
                :label="$t('form.fields.note') + ' *'"
                :error-messages="errors"
              ></v-textarea>
            </validation-provider>
          </v-col>
        </v-row>

        <v-row class="my-n12" v-if="!didVerify">
          <v-col>
            <div id="mcaptcha__widget-container" />
          </v-col>
        </v-row>

        <v-row class="mt-n4 mb-2" justify="end">
          <v-col cols="3" lg="2">
            <v-btn :disabled="invalid || !email" @click="submit">
              {{ $t("form.btn.send") }}
            </v-btn>
          </v-col>
        </v-row>
      </validation-observer>
    </v-form>
    <v-dialog v-model="dialog" width="500">
      <v-card>
        <v-card-title class="white--text headline primary" primary-title>
          {{ $t("form.confirmationDialog.title") }}
        </v-card-title>

        <v-card-text class="pa-md-4" v-if="contactType === 'mailingList'">
          {{ $t("form.confirmationDialog.confirmationTextMailingList") }}
        </v-card-text>
        <v-card-text class="pa-md-4" v-else>
          {{ $t("form.confirmationDialog.confirmationText") }}
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="reload">
            {{ $t("form.confirmationDialog.buttonText") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  formSubmissionResource,
  verificationResource,
  mcaptchaSettingsResource,
} from "@/api/resources";

export default {
  data: () => ({
    firstName: "",
    lastName: "",
    organization: "",
    name: "",
    email: "",
    subject: "",
    note: "",
    confirmationEmail: "",
    dialog: false,
    didVerify: false,
  }),
  async mounted() {
    const captchaSettings = (await mcaptchaSettingsResource.get()).data;
    let config = {
      widgetLink: new URL(captchaSettings.widgetLink),
    };
    // eslint-disable-next-line no-undef
    new mcaptchaGlue.default(config);
  },
  methods: {
    async submit() {
      if (!this.didVerify) {
        const verificationToken = document.getElementById("mcaptcha__token").value;
        if (!verificationToken) {
          return;
        }
        await verificationResource.post("", {
          token: verificationToken,
        });
      }
      this.didVerify = true;
      await formSubmissionResource.post("", {
        contactType: this.contactType,
        name: this.name,
        email: this.email,
        subject: this.subject,
        note: this.note,
        firstName: this.firstName,
        lastName: this.lastName,
        organization: this.organization,
      });
      this.dialog = true;
      this.reset();
    },

    reset() {
      this.firstName = "";
      this.lastName = "";
      this.organization = "";
      this.name = "";
      this.email = "";
      this.subject = "";
      this.note = "";
      this.confirmationEmail = "";
      this.$refs.validationObserver.reset();
    },

    reload() {
      location.reload();
    },
  },
  computed: {
    contactType() {
      return window.contactType || "contact";
    },
  },
};
</script>
